import { useCallback, useEffect, useState } from 'react'
import { api } from '../api.js'
import { useSitzung } from '../sitzung.jsx'
import Kopfzeile from '../Kopfzeile.jsx'
import Dialog from '../Dialog.jsx'
import Icon from '../Icon.jsx'
import Einstellungsgruppe from '../Einstellungsgruppe.jsx'
import Nullstellung from '../Nullstellung.jsx'
import { euro, heute } from '../format.js'

const ROLLENNAME = {
  mitarbeiter: 'Mitarbeiter',
  vorgesetzter: 'Vorgesetzter',
  admin: 'Verwaltung',
}

const ARTNAME = { minijob: 'Minijob', gehalt: 'Gehalt' }

// Kurztext und Symbol je Einstellungsgruppe — damit man nicht raten muss,
// was sich hinter welchem Knopf verbirgt.
const GRUPPENINFO = {
  Betrieb: { symbol: 'info', text: 'Name und Anschrift für den PDF-Kopf, Adresse der App' },
  Mailversand: { symbol: 'post', text: 'SMTP-Zugang für die Einladungen, mit Verbindungstest' },
  Zeiterfassung: { symbol: 'uhr', text: 'Zeitraster, Erfassung im Voraus, Pflichtfelder' },
  Monatsberichte: { symbol: 'senden', text: 'Wohin die unterschriebenen Blätter gehen' },
  Erinnerung: { symbol: 'kalender', text: 'Monatlicher Anstoß bei offenen Freigaben' },
  Minijob: { symbol: 'warnung', text: 'Verdienstgrenze und ab wann gewarnt wird' },
  Sicherheit: { symbol: 'schloss', text: 'Fehlversuche bis zur Sperre und Sperrdauer' },
}

// Konten anlegen und pflegen — dazu die Einstellungen der App, jede Gruppe
// hinter einem eigenen Knopf in einem Dialog.
export default function Verwaltung() {
  const { benutzer } = useSitzung()
  const [liste, setListe] = useState([])
  const [mailBereit, setMailBereit] = useState(true)
  const [einstellungen, setEinstellungen] = useState(null)
  const [offeneGruppe, setOffeneGruppe] = useState(null)
  const [neuOffen, setNeuOffen] = useState(false)
  const [meldung, setMeldung] = useState('')
  const [fehler, setFehler] = useState('')

  const laden = useCallback(() => {
    api('/mitarbeiter?alle=1')
      .then((d) => {
        setListe(d.mitarbeiter)
        setMailBereit(d.mailBereit !== false)
      })
      .catch((e) => setFehler(e.message))
  }, [])

  useEffect(() => {
    laden()
    api('/einstellungen')
      .then(setEinstellungen)
      .catch((e) => setFehler(e.message))
  }, [laden])

  // Nach dem Speichern im Dialog: Verzeichnis und Mailstand übernehmen.
  function einstellungenUebernehmen(antwort) {
    setEinstellungen((bisher) => ({ ...bisher, ...antwort }))
    setMailBereit(antwort.mail?.bereit ?? mailBereit)
  }

  function melde(text) {
    setMeldung(text)
    setFehler('')
  }

  return (
    <main className="huelle">
      <Kopfzeile />
      <h2 className="monatstitel monatstitel--links">Verwaltung</h2>

      {meldung && (
        <div className="meldung ok" role="status">
          <Icon name="haken" groesse={18} />
          <span>{meldung}</span>
        </div>
      )}
      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      {!mailBereit && (
        <div className="meldung warnung">
          <Icon name="post" groesse={18} />
          <span>
            Kein Mailversand eingerichtet. Zugänge lassen sich trotzdem anlegen — der
            Einrichtungslink erscheint dann hier zum Weitergeben.
          </span>
        </div>
      )}

      <section className="karte karte--liste">
        <h2>Zugänge</h2>
        {liste.map((person) => (
          <Kontozeile
            key={person.id}
            person={person}
            selbst={person.id === benutzer.id}
            mailBereit={mailBereit}
            beiErfolg={melde}
            beiFehler={setFehler}
            beiListe={laden}
          />
        ))}
        <div className="knopfreihe" style={{ margin: '1rem 0 .5rem' }}>
          <button className="knopf knopf--haupt" type="button" onClick={() => setNeuOffen(true)}>
            <Icon name="plus" /> Mitarbeiter anlegen
          </button>
        </div>
      </section>

      <section className="karte">
        <h2>Einstellungen</h2>
        {!einstellungen && <p className="hinweis">Einen Moment …</p>}

        <div className="gruppenliste">
          {einstellungen?.gruppen.map((gruppe) => (
            <button
              key={gruppe}
              className="gruppenknopf"
              type="button"
              onClick={() => setOffeneGruppe(gruppe)}
            >
              <span className="gruppenknopf__symbol">
                <Icon name={GRUPPENINFO[gruppe]?.symbol || 'zahnrad'} />
              </span>
              <span className="gruppenknopf__inhalt">
                <span className="gruppenknopf__name">{gruppe}</span>
                <span className="gruppenknopf__text">{GRUPPENINFO[gruppe]?.text || ''}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <Nullstellung
        beiFertig={() => {
          laden()
          melde('Das System wurde zurückgesetzt.')
        }}
      />

      {neuOffen && (
        <Neuanlage
          mailBereit={mailBereit}
          beiErfolg={melde}
          beiListe={laden}
          beiSchliessen={() => setNeuOffen(false)}
        />
      )}

      {offeneGruppe && einstellungen && (
        <Einstellungsgruppe
          gruppe={offeneGruppe}
          verzeichnis={einstellungen.einstellungen}
          mail={einstellungen.mail}
          beiGespeichert={einstellungenUebernehmen}
          beiSchliessen={() => setOffeneGruppe(null)}
        />
      )}
    </main>
  )
}

// --- Neues Konto ------------------------------------------------------------

function Neuanlage({ mailBereit, beiErfolg, beiListe, beiSchliessen }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [rolle, setRolle] = useState('mitarbeiter')
  const [art, setArt] = useState('minijob')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')
  const [link, setLink] = useState('')

  async function anlegen(ereignis) {
    ereignis.preventDefault()
    setLaeuft(true)
    setFehler('')
    try {
      const antwort = await api('/mitarbeiter', {
        methode: 'POST',
        koerper: { name, email, rolle, beschaeftigungsart: art },
      })
      beiListe()
      if (antwort.versandt) {
        beiErfolg(
          `${antwort.mitarbeiter.name} angelegt — Einladung an ${antwort.mitarbeiter.email} verschickt.`
        )
        beiSchliessen()
      } else {
        // Konto steht trotzdem. Link anzeigen statt still zu scheitern.
        setLink(antwort.link)
      }
    } catch (e) {
      setFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  if (link) {
    return (
      <Dialog titel="Zugang angelegt" symbol="haken" beiSchliessen={beiSchliessen}>
        <div className="meldung warnung">
          <Icon name="post" groesse={18} />
          <span>Die Mail ging nicht raus. Bitte den Link selbst weitergeben.</span>
        </div>
        <Linkanzeige link={link} />
        <div className="dialog__fuss">
          <button className="knopf knopf--haupt" type="button" onClick={beiSchliessen}>
            Fertig
          </button>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog titel="Neuer Zugang" symbol="person" beiSchliessen={beiSchliessen}>
      <form onSubmit={anlegen}>
        <div className="feld">
          <label htmlFor="neu-name">Name</label>
          <input
            id="neu-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="feld">
          <label htmlFor="neu-email">E-Mail</label>
          <input
            id="neu-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="feld">
          <label htmlFor="neu-rolle">Rolle</label>
          <select id="neu-rolle" value={rolle} onChange={(e) => setRolle(e.target.value)}>
            <option value="mitarbeiter">Mitarbeiter — sieht nur die eigenen Zeiten</option>
            <option value="vorgesetzter">Vorgesetzter — sieht alle, gibt frei</option>
            <option value="admin">Verwaltung — zusätzlich Stammdaten</option>
          </select>
        </div>

        <div className="feld">
          <label htmlFor="neu-art">Beschäftigung</label>
          <select id="neu-art" value={art} onChange={(e) => setArt(e.target.value)}>
            <option value="minijob">Minijob — mit Stundenlohn und Verdienstgrenze</option>
            <option value="gehalt">Gehalt — reine Zeiterfassung, ohne Beträge</option>
          </select>
        </div>

        <p className="hinweis">
          {mailBereit
            ? 'Die Person bekommt eine Mail mit einem Link und vergibt ihr Passwort selbst. Der Link gilt einmalig.'
            : 'Ohne Mailversand erscheint der Link nach dem Anlegen hier zum Weitergeben.'}
        </p>

        {fehler && (
          <div className="meldung fehler" role="alert" style={{ marginTop: '1rem' }}>
            <Icon name="warnung" groesse={18} />
            <span>{fehler}</span>
          </div>
        )}

        <div className="dialog__fuss">
          <button className="knopf knopf--leise" type="button" onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
            <Icon name="senden" /> {laeuft ? 'Wird angelegt …' : 'Anlegen und einladen'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

// Zeigt den Link zum Kopieren, wenn die Mail nicht rausging.
function Linkanzeige({ link }) {
  const [kopiert, setKopiert] = useState(false)

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(link)
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2500)
    } catch {
      // Ohne Zwischenablage bleibt der Link zum Markieren stehen.
    }
  }

  return (
    <div className="linkkasten">
      <p className="hinweis hinweis--oben">Einrichtungslink — nur einmal verwendbar:</p>
      <code className="link">{link}</code>
      <button className="knopf knopf--leise knopf--klein" type="button" onClick={kopieren}>
        <Icon name={kopiert ? 'haken' : 'dokument'} groesse={16} />
        {kopiert ? 'Kopiert' : 'Kopieren'}
      </button>
    </div>
  )
}

// --- Bestehendes Konto ------------------------------------------------------

function Kontozeile({ person, selbst, mailBereit, beiErfolg, beiFehler, beiListe }) {
  const [offen, setOffen] = useState(false)
  const [name, setName] = useState(person.name)
  const [rolle, setRolle] = useState(person.rolle)
  const [art, setArt] = useState(person.beschaeftigungsart)
  const [betrag, setBetrag] = useState('')
  const [ab, setAb] = useState(heute())
  const [link, setLink] = useState('')
  const [fragtAbschalten, setFragtAbschalten] = useState(false)
  const [laeuft, setLaeuft] = useState(false)

  async function fuehreAus(aktion) {
    setLaeuft(true)
    try {
      await aktion()
      beiListe()
    } catch (e) {
      beiFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  async function stammdaten(ereignis) {
    ereignis.preventDefault()
    await fuehreAus(async () => {
      await api(`/mitarbeiter/${person.id}`, {
        methode: 'PATCH',
        koerper: { name, rolle, beschaeftigungsart: art },
      })
      beiErfolg(`${name} gespeichert.`)
    })
  }

  async function lohnSetzen(ereignis) {
    ereignis.preventDefault()
    const cent = alsCent(betrag)
    if (cent === null || cent <= 0) return beiFehler('Bitte einen Betrag wie 13,90 eingeben.')
    await fuehreAus(async () => {
      await api(`/mitarbeiter/${person.id}/stundenlohn`, {
        methode: 'PUT',
        koerper: { betragCent: cent, gueltigAb: ab },
      })
      beiErfolg(`Stundenlohn für ${person.name} gespeichert.`)
      setBetrag('')
    })
  }

  async function neueEinladung() {
    await fuehreAus(async () => {
      const antwort = await api(`/mitarbeiter/${person.id}/einladung`, { methode: 'POST' })
      if (antwort.versandt) beiErfolg(`Neuer Link an ${person.email} verschickt.`)
      else setLink(antwort.link)
    })
  }

  async function aktivUmschalten() {
    await fuehreAus(async () => {
      await api(`/mitarbeiter/${person.id}`, {
        methode: 'PATCH',
        koerper: { aktiv: !person.aktiv },
      })
      beiErfolg(`${person.name} ist jetzt ${person.aktiv ? 'deaktiviert' : 'wieder aktiv'}.`)
      setFragtAbschalten(false)
    })
  }

  return (
    <div className={`konto ${person.aktiv ? '' : 'konto--inaktiv'}`}>
      <div className="konto__kopf">
        <div className="konto__name">
          <strong>{person.name}</strong>
          <small>{person.email}</small>
          <span className="konto__marker">
            <span className="abzeichen abzeichen--offen">{ROLLENNAME[person.rolle]}</span>
            <span className="abzeichen abzeichen--offen">{ARTNAME[person.beschaeftigungsart]}</span>
            {person.stundenlohnCent !== null && (
              <span className="abzeichen abzeichen--offen">
                {euro(person.stundenlohnCent)}/Std.
              </span>
            )}
            {person.einladungOffen && (
              <span className="abzeichen abzeichen--eingereicht">Einladung offen</span>
            )}
            {!person.aktiv && (
              <span className="abzeichen abzeichen--zurueckgewiesen">deaktiviert</span>
            )}
          </span>
        </div>
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={() => setOffen(!offen)}
          title={offen ? 'Zuklappen' : 'Bearbeiten'}
        >
          <Icon name={offen ? 'runter' : 'stift'} />
          <span className="nur-vorlesen">{offen ? 'Zuklappen' : 'Bearbeiten'}</span>
        </button>
      </div>

      {link && <Linkanzeige link={link} />}

      {offen && (
        <div className="konto__inhalt">
          <form onSubmit={stammdaten}>
            <div className="feld">
              <label>Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="formularreihe formularreihe--eng">
              <div className="feld">
                <label>Rolle</label>
                <select value={rolle} onChange={(e) => setRolle(e.target.value)} disabled={selbst}>
                  <option value="mitarbeiter">Mitarbeiter</option>
                  <option value="vorgesetzter">Vorgesetzter</option>
                  <option value="admin">Verwaltung</option>
                </select>
              </div>
              <div className="feld">
                <label>Beschäftigung</label>
                <select value={art} onChange={(e) => setArt(e.target.value)}>
                  <option value="minijob">Minijob</option>
                  <option value="gehalt">Gehalt</option>
                </select>
              </div>
            </div>
            <button className="knopf knopf--leise knopf--klein" type="submit" disabled={laeuft}>
              <Icon name="haken" groesse={16} /> Speichern
            </button>
          </form>

          {/* Stundenlohn gibt es nur beim Minijob — Gehaltsempfänger werden
              nicht auf Stundenbasis abgerechnet. */}
          {person.beschaeftigungsart === 'minijob' ? (
            <form onSubmit={lohnSetzen} style={{ marginTop: '1.25rem' }}>
              <div className="formularreihe formularreihe--eng">
                <div className="feld feld--schmal">
                  <label>Euro/Std.</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={betrag}
                    onChange={(e) => setBetrag(e.target.value)}
                    placeholder="13,90"
                  />
                </div>
                <div className="feld">
                  <label>gültig ab</label>
                  <input type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
                </div>
              </div>
              <button className="knopf knopf--leise knopf--klein" type="submit" disabled={laeuft}>
                <Icon name="haken" groesse={16} /> Lohn setzen
              </button>
            </form>
          ) : (
            <p className="hinweis">
              Gehalt: reine Zeiterfassung. Kein Stundenlohn, kein Verdienst, keine Verdienstgrenze.
            </p>
          )}

          <div className="knopfreihe">
            <button
              className="knopf knopf--leise knopf--klein"
              type="button"
              disabled={laeuft || !person.aktiv}
              onClick={neueEinladung}
            >
              <Icon name="post" groesse={16} />
              {mailBereit ? 'Neuen Link mailen' : 'Neuen Link erzeugen'}
            </button>
            {!selbst && (
              <button
                className={`knopf knopf--klein ${person.aktiv ? 'knopf--geist' : 'knopf--leise'}`}
                type="button"
                disabled={laeuft}
                onClick={() => (person.aktiv ? setFragtAbschalten(true) : aktivUmschalten())}
              >
                <Icon name={person.aktiv ? 'kreuz' : 'haken'} groesse={16} />
                {person.aktiv ? 'Deaktivieren' : 'Wieder aktivieren'}
              </button>
            )}
          </div>

          {selbst && (
            <p className="hinweis">
              Am eigenen Konto lassen sich Rolle und Aktiv-Zustand nicht ändern — sonst könnte sich
              die letzte Verwaltung selbst aussperren.
            </p>
          )}
        </div>
      )}

      {fragtAbschalten && (
        <Dialog
          titel="Zugang deaktivieren"
          symbol="kreuz"
          beiSchliessen={() => setFragtAbschalten(false)}
        >
          <p>
            <strong>{person.name}</strong> kann sich danach nicht mehr anmelden.
          </p>
          <p className="hinweis">
            Erfasste Zeiten bleiben vollständig erhalten und weiterhin einsehbar. Der Zugang lässt
            sich jederzeit wieder aktivieren.
          </p>
          <div className="dialog__fuss">
            <button
              className="knopf knopf--leise"
              type="button"
              onClick={() => setFragtAbschalten(false)}
            >
              Abbrechen
            </button>
            <button
              className="knopf knopf--gefahr"
              type="button"
              onClick={aktivUmschalten}
              disabled={laeuft}
            >
              <Icon name="kreuz" /> Deaktivieren
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

// Nimmt "13,90" ebenso wie "13.90" — auf dem Handy ist das Komma näher.
function alsCent(text) {
  const bereinigt = String(text).replace(',', '.').trim()
  if (!/^\d+(\.\d{1,2})?$/.test(bereinigt)) return null
  return Math.round(Number(bereinigt) * 100)
}
